import { useEffect } from 'react';

import getConfig from 'next/config';
import { useCachedBasketData } from '@JohnLewisPartnership/jl-design-system/utils/basket/cachedBasket';
import { setJLData } from './setJLData';
import { getAnalyticsData } from './utils/transformations';

import {
  useAnalyticsContext,
  useBreadcrumbContext,
  useCompareContext,
  useFacetContext,
  useProductContext,
  useRouterContext,
  useSearchContext,
  useVertexContext,
} from '../../context/PlpContext';
import { useExperiencesContext } from '../experiences/Experiences';
import { createDebugger } from '../debug/Debug';

const debug = createDebugger('analytics');
import { ANALYTICS_JLDATA_UPDATED } from './events';
import { useAnalyticsCompareUpdate } from './update-hooks/useAnalyticsCompareUpdate';
import { useAnalyticsProductUpdate } from './update-hooks/useAnalyticsProductUpdate';
import { useAnalyticsProductCardViewUpdate } from './update-hooks/useAnalyticsProductCardViewUpdate';

export const Analytics = ({ page }) => {
  const [, updateTimestamp] = useAnalyticsContext();

  const compare = useCompareContext();
  const search = useSearchContext();
  const { breadcrumbs } = useBreadcrumbContext();
  const facets = useFacetContext();
  const productData = useProductContext();
  const vertex = useVertexContext();
  const { isChunkSizeExperiment, isStandaloneCompareFlagOn } =
    useExperiencesContext();
  const { query } = useRouterContext();
  const { chunk = '1' } = query;

  const { publicRuntimeConfig } = getConfig();
  const {
    chunkSize: chunksize,
    chunkSizeExperiment,
    pageSize,
  } = publicRuntimeConfig;
  const chunkSize = isChunkSizeExperiment ? chunkSizeExperiment : chunksize;

  const serialiseDependencies = (value) => JSON.stringify(value);

  const compareUpdates = useAnalyticsCompareUpdate();
  const productUpdates = useAnalyticsProductUpdate();
  const productCardViewUpdates = useAnalyticsProductCardViewUpdate();
  const basketUpdates = useCachedBasketData();

  const dependencies = [
    serialiseDependencies(compareUpdates),
    serialiseDependencies(productUpdates),
    serialiseDependencies(productCardViewUpdates),
    serialiseDependencies(basketUpdates),
  ];

  useEffect(() => {
    const analytics = getAnalyticsData({
      chunk,
      chunkSize,
      compare,
      facets,
      isChunkSizeExperiment,
      isStandaloneCompareFlagOn,
      page,
      pageSize,
      productData,
      routerQuery: query,
      search,
      vertex,
    });
    setJLData(breadcrumbs, analytics, search, page);
    updateTimestamp();
    debug(`dispatching event "${ANALYTICS_JLDATA_UPDATED}"`);
    document.body.dispatchEvent(new CustomEvent(ANALYTICS_JLDATA_UPDATED));
  }, dependencies);

  return null;
};
